<script setup lang="ts">
import { useI18n } from "vue-i18n";

/**
 * ChoseSection Component
 *
 * Description: A reusable Vue component designed to display a selection section with two options.
 *
 * Props:
 *   - data: An object containing the following properties:
 *     - center: An object with the following properties:
 *       - firstParagraph: A string representing the first paragraph of text.
 *       - list: An array of strings representing the list items.
 *       - benefits: An array of objects with the following properties:
 *         - icon: A string representing the icon URL.
 *         - text: A string representing the benefit text.
 *
 * Data:
 *   - rightActive: A boolean indicating whether the right option is active.
 *   - leftActive: A boolean indicating whether the left option is active.
 *   - leftMobileActive: A boolean indicating whether the left option is active on mobile devices.
 *   - rightMobileActive: A boolean indicating whether the right option is active on mobile devices.
 *
 * Methods:
 *   - mobileActive(side: string): A function that toggles the active state of the left or right option on mobile devices.
 */
const { t } = useI18n();
const rightActive = ref(true);
const leftActive = ref(true);
const leftMobileActive = ref(false);
const rightMobileActive = ref(false);
/**
 * Toggles the active state of the left or right option on mobile devices.
 *
 * @param {string} side - The side of the option to toggle. Can be either "right" or "left".
 */
const mobileActive = (side: string) => {
  if (side === "right") {
    rightActive.value = !rightActive.value;

    leftActive.value = false;
    leftMobileActive.value = false;
    rightMobileActive.value = !rightMobileActive.value;
  } else {
    rightActive.value = false;
    leftActive.value = !leftActive.value;

    leftMobileActive.value = !leftMobileActive.value;
    rightMobileActive.value = false;
  }
};
const { data } = defineProps<{
  data: {
    center: {
      firstParagraph?: string;
      secondParagraph?: string;
      thirdParagraph?: string;
      fourthParagraph?: string;
      subTitle?: string;
      list?: string[];
      benefits: { icon: string; text: string }[];
    };
  };
}>();
</script>
<template>
  <section class="chose-section">
    <!-- left desktop -->
    <div
      class="cursor-hover"
      :class="{ leftActive: leftActive }"
      @click="leftActive = !leftActive"
    >
      <div class="left desktop">
        <img
          class="white-arrow"
          src="@/assets/img/ui-insurance/white-arrow.png"
          :alt="t('alt.arrow')"
        />
        <div class="left-title">
          <div class="exclamation">
            <img
              src="@/assets/img/ui-insurance/exclamation.png"
              :alt="$t('alt.exclamation')"
            />
          </div>
          <h3>
            {{ $t("UiServiceDetail.choseSection.left.title.part-1")
            }}<br class="mobile-break" />
            {{ $t("UiServiceDetail.choseSection.left.title.part-2") }}
          </h3>
        </div>
        <p class="paragraph"><slot name="leftParagraph"></slot></p>
      </div>
    </div>
    <!-- center desktop -->
    <div class="center">
      <h2 class="title">
        <slot name="title"></slot>
      </h2>
      <h3 v-if="!data.center.subTitle" class="subTitle">
        {{ $t("UiServiceDetail.choseSection.center.subTitle") }}
      </h3>
      <h3 v-if="data.center.subTitle" class="subTitle">
        {{ data.center.subTitle }}
      </h3>
      <p v-if="data.center.firstParagraph" class="firstParagraph">
        {{ data.center.firstParagraph }}
      </p>
      <ul v-if="data.center.list" class="list">
        <li
          v-for="(item, index) in data.center.list"
          :key="index"
          class="list-item"
        >
          {{ item }}
        </li>
      </ul>
      <p v-if="data.center.secondParagraph" class="secondParagraph-center">
        {{ data.center.secondParagraph }}
      </p>
      <p
        v-if="data.center.thirdParagraph"
        class="secondParagraph-center thirdParagraph-center"
      >
        {{ data.center.thirdParagraph }}
      </p>
      <p
        v-if="data.center.fourthParagraph"
        class="secondParagraph-center thirdParagraph-center"
      >
        {{ data.center.fourthParagraph }}
      </p>
      <div class="bottom">
        <p class="secondParagraph">
          {{ $t("UiServiceDetail.choseSection.center.secondParagraph") }}
          <img
            src="@/assets/img/ui-insurance/tutassur.svg"
            alt="tutassur"
            class="tutassur"
          />
          !
        </p>
        <div class="benefits">
          <div v-for="(item, index) in data.center.benefits" :key="index">
            <img :src="item.icon" :alt="$t('alt.benefits')" />
            <p>{{ item.text }}</p>
          </div>
        </div>
      </div>
    </div>
    <!-- mobile -->
    <div class="mobile">
      <!-- left mobile -->
      <div
        class="mobile-left"
        :class="[
          { mobileLeftActive: leftMobileActive },
          { mobileRightActive: rightMobileActive },
        ]"
      >
        <!-- <div class="under"></div> -->
        <div class="mobile-top">
          <div
            class="top top-left"
            :class="{ leftRotate: leftMobileActive }"
            @click="mobileActive('left')"
          >
            <img
              class="mobile-white-arrow"
              src="@/assets/img/ui-insurance/white-arrow.png"
              :alt="t('alt.arrow')"
            />
            <div class="exclamation">
              <img
                src="@/assets/img/ui-insurance/exclamation.png"
                :alt="$t('alt.exclamation')"
              />
            </div>
            <h3>
              {{ $t("UiServiceDetail.choseSection.left.title.part-1")
              }}<br class="mobile-break" />
              {{ $t("UiServiceDetail.choseSection.left.title.part-2") }}
            </h3>
          </div>
          <div
            class="top top-right"
            :class="{ rightRotate: rightMobileActive }"
            @click="mobileActive('right')"
          >
            <img
              class="mobile-white-arrow"
              src="@/assets/img/ui-insurance/white-arrow.png"
              :alt="t('alt.arrow')"
            />
            <div class="interrogation">
              <img
                src="@/assets/img/ui-insurance/interrogation.png"
                :alt="$t('alt.interrogation')"
              />
            </div>
            <h3>
              {{ $t("UiServiceDetail.choseSection.right.title.part-1")
              }}<br class="mobile-break" />
              {{ $t("UiServiceDetail.choseSection.right.title.part-2") }}
            </h3>
          </div>
          <!-- <div class="div-raduis">
            <div class="child-raduis"></div>
          </div> -->
        </div>

        <div class="content content-left">
          <p class="mobile-paragraph"><slot name="leftParagraph"></slot></p>
        </div>
        <div class="content content-right">
          <p class="mobile-paragraph"><slot name="rightParagraph"></slot></p>
        </div>
      </div>
    </div>
    <!-- right desktop -->
    <div
      class="cursor-hover"
      :class="{ rightActive: rightActive }"
      @click="rightActive = !rightActive"
    >
      <div class="right desktop">
        <img
          class="white-arrow"
          src="@/assets/img/ui-insurance/white-arrow.png"
          :alt="t('alt.arrow')"
        />

        <p class="paragraph"><slot name="rightParagraph"></slot></p>

        <div class="right-title">
          <div class="interrogation">
            <img
              src="@/assets/img/ui-insurance/interrogation.png"
              :alt="$t('alt.interrogation')"
            />
          </div>
          <h3>
            {{ $t("UiServiceDetail.choseSection.right.title.part-1")
            }}<br class="mobile-break" />
            {{ $t("UiServiceDetail.choseSection.right.title.part-2") }}
          </h3>
        </div>
      </div>
    </div>
  </section>
</template>
<style scoped>
/* center section */
.center {
  padding: 2rem 1rem;
  width: 43rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  /* margin: auto; */
  background: var(--vt-c-purple) 0% 0% no-repeat;
  box-shadow: 5px 10px 10px var(--vt-c-deep-purple);
  border-radius: 49px;
  opacity: 1;
  position: relative;
  z-index: 2; /* Ensures .center stays on top */
}
.center * {
  color: var(--vt-c-white);
  text-align: center;
}
.center .title {
  font-family: "Gilmer Bold";
  margin-bottom: 0;
  padding-bottom: 0;
  font-size: clamp(2rem, 5vw, 47px);
  line-height: 65px;
}
.center .subTitle {
  margin-top: 1rem;
  font: normal normal normal 33px/33px "Belgates";
}
.center .firstParagraph {
  width: 80%;
  margin: auto;
  font-family: "Gilmer Heavy";
  font-size: 14px;
  line-height: 28px;
}
.center ul {
  margin: auto;
  margin-top: 2rem;
  width: 80%;
}
.center ul li,
.secondParagraph-center {
  text-align: left;
  font-family: "Gilmer Light";
  font-size: 14px;
  line-height: 26px;
}
.secondParagraph-center {
  margin-top: 2rem;
  width: 80%;
}
.thirdParagraph-center {
  margin-top: 0;
}
.center .bottom .secondParagraph {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3px;
  font-size: 20px;
  line-height: 28px;
  font-family: "Gilmer Heavy";
  text-align: center;
  flex-wrap: nowrap;
  white-space: nowrap;
}

::v-deep(.tutassur) {
  object-fit: contain;
  width: 87px;
  height: 26px;
}
.center .bottom .benefits {
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-top: 2rem;
  margin-bottom: 3rem;
}
.center .bottom .benefits p {
  font: normal normal normal 15px/18px "Gilmer Light";
  width: 182px;
  margin: auto;
  margin-top: 0.5rem;
}
section {
  position: relative;
  padding: 0;
  margin-top: 4rem;
  display: flex;
  justify-content: center;
  align-items: center;
}
/* left  */
.left,
.right {
  position: absolute;
  background: var(--vt-c-light-purple) 0% 0% no-repeat;
  box-shadow: 5px 10px 10px var(--vt-c-deep-purple);
  border-radius: 49px;
  width: 532px;
  height: 650px;
  top: calc(50% - 325px);
  z-index: 1;
  transition: all 2s ease;
}

.left {
  left: calc(50% - 765px);
}

.right,
.left {
  text-align: right;
  float: right;
  right: calc(50% - 765px);
  display: flex;
  align-items: center;
  justify-content: end;
}
.rightActive .right {
  right: calc(50% - 485px);
}
.leftActive .left {
  left: calc(50% - 485px);
}
.white-arrow {
  right: 23.5rem;
  position: absolute;
  transition: all 2s ease;
}
.rightActive .white-arrow {
  right: 6rem;
  transform: rotate(180deg);
}

.right-title,
.left-title {
  position: relative;
  align-self: flex-start;
  width: 130px;
}
.right-title .interrogation,
.left-title .exclamation {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: auto;
  margin-top: 2rem;
  margin-bottom: 6rem;
  border: 1px solid var(--vt-c-white);
  padding: 0; /* Remove unnecessary padding */
  border-radius: 50%;
  height: 50px;
  width: 50px;
}
.right-title .interrogation img,
.left-title .exclamation img {
  width: 30%;
  height: auto;
  margin: 0;
  display: block;
}

.right-title .interrogation img,
.left-title .exclamation img {
  margin: auto;
  padding: 0;
}

.right h3,
.left h3 {
  white-space: nowrap;
  color: var(--vt-c-white);
  transform: rotate(90deg);
  font-size: 30px;
  line-height: 54px;
  font-family: "Gilmer Bold";
}
.right .paragraph,
.left .paragraph {
  font-size: 18px;
  line-height: 23px;
  font-family: "Gilmer Light";
  width: 42.5%;
  color: var(--vt-c-white);
  text-align: end;
  margin: 0;
  /* height: 100%; */
}
::v-deep(.benefits-span) {
  color: #7cf5ff;
  font-weight: 800;
  font-family: "Gilmer Heavy", sans-serif;
}

/* left */
.left {
  float: left;
  justify-content: start;
}
.left h3 {
  transform: rotate(-90deg);
}
.left-title .exclamation {
  margin-bottom: 20rem;
}
.left .paragraph {
  text-align: start;
}
.left .white-arrow {
  right: unset;
  left: 23.5rem;
  transform: rotate(180deg);
}
.leftActive .white-arrow {
  left: 6rem;
  transform: rotate(0deg);
}
.mobile {
  display: none;
}
@media screen and (max-width: 1600px) {
  .center {
    padding: 1rem 0.5rem;
    width: 35rem;
  }
  .center .title {
    font-size: 2rem;
    line-height: 2rem;
  }
  .center .subTitle {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    font-size: 1.5rem;
  }
  .center .firstParagraph {
    font-size: 12px;
    line-height: 18px;
    margin-bottom: 0.5rem;
  }
  .center .bottom .benefits {
    gap: 1rem;
    margin-bottom: 2rem;
  }
  .left-title,
  .right-title {
    width: 80px;
  }
  .left-title h3,
  .right-title h3 {
    font-size: 20px;
  }
  .left,
  .right {
    height: 500px;
    width: 500px;
    top: calc(50% - 250px);
  }
  .right {
    right: calc(50% - 600px);
  }
  .left {
    left: calc(50% - 600px);
  }
  .left .white-arrow {
    left: 17.7rem;
  }
  .leftActive .white-arrow {
    left: 3.4rem;
  }
  .right .white-arrow {
    right: 17.8rem;
  }
  .rightActive .white-arrow {
    right: 3.2rem;
  }
  .leftActive .left {
    left: calc(50% - 368px);
  }
  .rightActive .right {
    right: calc(50% - 368px);
  }
  .right .paragraph,
  .left .paragraph {
    font-size: 14px;
  }
  .left .paragraph {
    padding-left: 0.4rem;
  }
  .right-title .interrogation,
  .left-title .exclamation {
    width: 50px;
    height: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
  }
  .right-title .interrogation {
    margin-bottom: 4.5rem;
  }
  .left .exclamation {
    margin-bottom: 14rem;
  }
  .right-title .interrogation img,
  .left-title .exclamation img {
    width: 15px;
    height: auto;
    margin: unset;
  }
  .center .bottom .secondParagraph {
    font-size: 1.3rem;
  }
  .center .bottom .benefits p {
    font-size: 0.7rem;
    width: 150px;
  }
  .center .bottom .benefits img {
    width: 50px;
    height: 50px;
  }
  .center ul li,
  .secondParagraph-center {
    font-size: 0.75rem;
    line-height: 1.6rem;
  }
}
@media screen and (max-width: 1250px) {
  .center {
    padding: 0.5rem 0;
    width: 27rem;
    height: 870px;
  }
  .center .bottom .benefits {
    flex-wrap: wrap;
    margin-bottom: 1rem;
  }
  .left .paragraph,
  .right .paragraph {
    font-size: 11px;
    width: 41%;
  }
  .left,
  .right {
    height: 730px;
    width: 18rem;
    top: calc(50% - 365px);
  }
  .left {
    left: calc(50% - 430px);
  }
  .right {
    right: calc(50% - 430px);
  }
  .rightActive .right {
    right: calc(50% - 290px);
  }
  .leftActive .left {
    left: calc(50% - 290px);
  }
  .right h3,
  .left h3 {
    position: relative;
    top: 100px;
    /* line-height: 265px; */
  }
  .left .white-arrow {
    left: 11.5rem;
  }
  .leftActive .white-arrow {
    left: 3.2rem;
  }
  .right .white-arrow {
    right: 11.5rem;
  }
  .rightActive .white-arrow {
    right: 3.2rem;
  }
  .secondParagraph-center {
    margin-top: 0;
  }
}

@media screen and (max-width: 960px) {
  section {
    margin-top: 50px;
  }
}
@media screen and (max-width: 900px) {
  section {
    flex-direction: column;
  }
  .secondParagraph-center {
    margin-top: 2rem;
  }
  .thirdParagraph-center {
    margin-top: 0;
  }
  .left,
  .right {
    display: none;
  }
  .mobile {
    display: block;
    /* grid-template-columns: 1fr 1fr; */
    width: 80%;
    gap: 0.5rem;
    position: relative;
  }

  .center .title {
    font-size: 25px;
    line-height: 35px;
  }
  .center .subTitle {
    font-size: 25px;
    line-height: 51px;
  }
  .center .firstParagraph,
  .centr .secondParagraph {
    margin: auto;
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
    width: 92%;
    text-align: center;
    font-size: 17px;
    line-height: 23px;
  }
  .center {
    width: 80%;
    padding: 1rem;
    height: unset;
    border-radius: 25px;
  }
  .center ul li,
  .secondParagraph-center {
    font-size: 17px;
    line-height: 23px;
  }
  .center .bottom .benefits {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  .benefits div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
  }
  .benefits > div:nth-child(3) {
    margin-top: 1.5rem;
    grid-column: 1 / -1;
    justify-self: center;
    text-align: center;
  }
  .center .bottom .benefits {
    margin-bottom: 1.5rem;
  }
  .center .bottom .benefits img {
    width: unset;
    height: unset;
  }
  .center .bottom .benefits p {
    font-size: 12px;
    line-height: 15px;
    margin: unset;
  }
  /* bottom mobile */
  .interrogation,
  .exclamation {
    margin-top: 0.8rem;
    margin-bottom: 0.5rem;
    border: 1px solid var(--vt-c-white);
    width: 49px;
    height: 49px;
    background: transparent;
    display: flex;
    justify-content: center;
    border-radius: 50%;
    align-items: center;
  }
  .interrogation img,
  .exclamation img {
    margin: unset;
    width: 20px;
  }

  .mobile-white-arrow {
    margin-top: 0.9rem;
    /* margin-bottom: 1.5rem;
    width: 19px;
    height: 19px; */
    rotate: -90deg;
  }
  .mobile-paragraph {
    font-family: "Gilmer Light";
  }
  .content {
    /* display: none; */
    max-height: 0;
    overflow: hidden;
    padding: 0;
    transition:
      all 1s ease,
      background-color 1s ease 2s,
      box-shadow 1s ease 2s,
      border-radius 1s ease 2s,
      color 1s ease 2s,
      top 1s ease 2s;
  }
  .mobileLeftActive .content,
  .mobileRightActive .content {
    transition: all 1s ease;
  }

  .mobileLeftActive .content-left,
  .mobileRightActive .content-right {
    max-height: 500px;
    padding: 1rem;
    overflow: unset;
  }

  .mobileRightActive .content,
  .mobileLeftActive .content {
    border-radius: 25px;
    text-align: left;
    font-size: 17px;
    line-height: 23px;
    font-family: "Gilmer Light";
    color: var(--vt-c-white);
    background: var(--vt-c-light-purple) 0% 0% no-repeat padding-box;
    width: 100%;
    position: relative;
    top: -1px;
    overflow: hidden;
    top: 14px;
    box-sizing: border-box;
  }
  .mobileRightActive .content-right {
    display: block;
    text-align: end;
  }
  .top {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    position: relative;
    transition: all 1s ease;
    margin-top: -0.5rem;
    background: var(--vt-c-light-purple) 0% 0% no-repeat padding-box;
    box-shadow: 5px 10px 10px var(--vt-c-deep-purple);
    border-radius: 25px;
    padding-bottom: 1rem;
    -webkit-tap-highlight-color: transparent;
  }
  .mobile-top {
    display: grid;
    gap: 0.7rem;
    grid-template-columns: 1fr 1fr;
  }

  .top-right::before {
    opacity: 0;
    content: "";
    height: 90%;
    width: 90%;
    position: absolute;
    border: 10px solid var(--vt-c-light-purple);
    border-top: unset;
    border-right: unset;
    border-bottom-left-radius: 35px;
    background-color: transparent;
    z-index: 3;
    left: -22px;
    bottom: -25px;
    transition: all 1s ease;
  }
  .top-left::before {
    transition: all 1s ease;

    opacity: 0;
    content: "";
    height: 90%;
    width: 90%;
    position: absolute;
    border: 10px solid var(--vt-c-light-purple);
    border-top: unset;
    border-left: unset;
    border-bottom-right-radius: 35px;
    background-color: transparent;
    z-index: 3;
    right: -22px;
    bottom: -25px;
  }
  .mobileRightActive .top-left::before,
  .mobileLeftActive .top-right::before {
    opacity: 1;
  }
  .top:not(.top-right)::after,
  .top:not(.top-left)::after {
    opacity: 0;
    bottom: -1rem;
    content: "";
    width: 100%;
    height: 1.2rem;
    position: absolute;
    background-color: var(--vt-c-light-purple);
    transition: all 1s ease;
    /* box-shadow: 5px 10px 10px var(--vt-c-deep-purple); */
  }
  .mobileRightActive .top:not(.top-left)::after,
  .mobileLeftActive .top:not(.top-right)::after {
    opacity: 1;
  }
  .mobileRightActive .top:not(.top-left)::after {
    box-shadow: 5px 10px 10px var(--vt-c-deep-purple);
  }

  .mobileLeftActive,
  .mobileRightActive {
    display: flex;
    flex-direction: column;
    /* gap: 1rem; */
    /* background-color: var(--vt-c-light-purple); */
  }
  .leftRotate .mobile-white-arrow,
  .rightRotate .mobile-white-arrow {
    rotate: 90deg;
  }
  .mobileLeftActive .top:not(.top-right) {
    /* width: 50%; */
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  .mobileRightActive .top:not(.top-left) {
    /* width: 50%; */
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  .mobileLeftActive .content {
    border-top-left-radius: 0;
    box-shadow: 5px 10px 10px var(--vt-c-deep-purple);
    z-index: 1;
    position: relative;
  }
  .mobileRightActive .content {
    border-top-right-radius: 0;
    box-shadow: 5px 10px 10px var(--vt-c-deep-purple);
    z-index: 1;
    position: relative;
  }
  .mobile h3 {
    text-align: center;
    margin: 0;
    font-family: "Gilmer Bold";
    font-size: 18px;
    color: var(--vt-c-white);
  }
  .mobile-white-arrow {
    transition: rotate 1s ease;
  }
}
@media screen and (max-width: 780px) {
  section {
    margin-top: 0;
  }
}
@media screen and (max-width: 500px) {
  .center .bottom .secondParagraph {
    flex-wrap: wrap;
  }
}
</style>
